import { Injectable } from '@angular/core';
import { CropperPosition, Dimensions, MoveStart } from '../interfaces/image-cropper.interfaces';

@Injectable({
  providedIn: 'root',
})
export class CropperPositionService {
  /**
   * Initializes cropper position with default values
   */
  public initCropperPosition(): CropperPosition {
    return {
      x1: -100,
      y1: -100,
      x2: 10000,
      y2: 10000,
    };
  }

  /**
   * Resets cropper position based on image dimensions and settings
   */
  public resetCropperPosition(
    sourceImageElement: HTMLElement,
    cropper: CropperPosition,
    maintainAspectRatio: boolean,
    aspectRatio: number,
    cropperStaticWidth: number,
    cropperStaticHeight: number
  ): void {
    if (cropperStaticHeight && cropperStaticWidth) {
      cropper.x1 = 0;
      cropper.x2 =
        sourceImageElement.offsetWidth > cropperStaticWidth ? cropperStaticWidth : sourceImageElement.offsetWidth;
      cropper.y1 = 0;
      cropper.y2 =
        sourceImageElement.offsetHeight > cropperStaticHeight ? cropperStaticHeight : sourceImageElement.offsetHeight;
    } else if (!maintainAspectRatio) {
      cropper.x1 = 0;
      cropper.x2 = sourceImageElement.offsetWidth;
      cropper.y1 = 0;
      cropper.y2 = sourceImageElement.offsetHeight;
    } else if (sourceImageElement.offsetWidth / aspectRatio < sourceImageElement.offsetHeight) {
      cropper.x1 = 0;
      cropper.x2 = sourceImageElement.offsetWidth;
      const cropperHeight: number = sourceImageElement.offsetWidth / aspectRatio;
      cropper.y1 = (sourceImageElement.offsetHeight - cropperHeight) / 2;
      cropper.y2 = cropper.y1 + cropperHeight;
    } else {
      cropper.y1 = 0;
      cropper.y2 = sourceImageElement.offsetHeight;
      const cropperWidth: number = sourceImageElement.offsetHeight * aspectRatio;
      cropper.x1 = (sourceImageElement.offsetWidth - cropperWidth) / 2;
      cropper.x2 = cropper.x1 + cropperWidth;
    }
  }

  /**
   * Resizes cropper position when window is resized
   */
  public resizeCropperPosition(cropper: CropperPosition, maxSize: Dimensions, sourceImageElement: HTMLElement): void {
    if (maxSize.width !== sourceImageElement.offsetWidth || maxSize.height !== sourceImageElement.offsetHeight) {
      cropper.x1 = (cropper.x1 * sourceImageElement.offsetWidth) / maxSize.width;
      cropper.x2 = (cropper.x2 * sourceImageElement.offsetWidth) / maxSize.width;
      cropper.y1 = (cropper.y1 * sourceImageElement.offsetHeight) / maxSize.height;
      cropper.y2 = (cropper.y2 * sourceImageElement.offsetHeight) / maxSize.height;
    }
  }

  /**
   * Checks and corrects cropper position to stay within bounds
   */
  public checkCropperPosition(cropper: CropperPosition, maxSize: Dimensions, maintainSize: boolean = false): void {
    if (cropper.x1 < 0) {
      const adjustment: number = maintainSize ? cropper.x1 : 0;
      cropper.x2 -= adjustment;
      cropper.x1 = 0;
    }
    if (cropper.y1 < 0) {
      const adjustment: number = maintainSize ? cropper.y1 : 0;
      cropper.y2 -= adjustment;
      cropper.y1 = 0;
    }
    if (cropper.x2 > maxSize.width) {
      const adjustment: number = maintainSize ? cropper.x2 - maxSize.width : 0;
      cropper.x1 -= adjustment;
      cropper.x2 = maxSize.width;
    }
    if (cropper.y2 > maxSize.height) {
      const adjustment: number = maintainSize ? cropper.y2 - maxSize.height : 0;
      cropper.y1 -= adjustment;
      cropper.y2 = maxSize.height;
    }
  }

  /**
   * Calculates scaled minimum size for cropper
   */
  public calculateScaledMinSize(
    cropperMinWidth: number,
    cropperMinHeight: number,
    transformedImage: HTMLImageElement,
    maxSize: Dimensions,
    maintainAspectRatio: boolean,
    aspectRatio: number
  ): { width: number; height: number } {
    const cropperScaledMinWidth: number =
      cropperMinWidth > 0 ? Math.max(20, (cropperMinWidth / transformedImage.width) * maxSize.width) : 20;

    let cropperScaledMinHeight: number;
    if (maintainAspectRatio) {
      cropperScaledMinHeight = Math.max(20, cropperScaledMinWidth / aspectRatio);
    } else if (cropperMinHeight > 0) {
      cropperScaledMinHeight = Math.max(20, (cropperMinHeight / transformedImage.height) * maxSize.height);
    } else {
      cropperScaledMinHeight = 20;
    }

    return {
      width: cropperScaledMinWidth,
      height: cropperScaledMinHeight,
    };
  }

  /**
   * Moves the cropper based on mouse/touch movement
   */
  public moveCropper(cropper: CropperPosition, moveStart: MoveStart, diffX: number, diffY: number): void {
    cropper.x1 = moveStart.x1 + diffX;
    cropper.y1 = moveStart.y1 + diffY;
    cropper.x2 = moveStart.x2 + diffX;
    cropper.y2 = moveStart.y2 + diffY;
  }

  /**
   * Resizes the cropper based on position and movement
   */
  public resizeCropper(
    cropper: CropperPosition,
    moveStart: MoveStart,
    diffX: number,
    diffY: number,
    cropperScaledMinWidth: number,
    cropperScaledMinHeight: number,
    maintainAspectRatio: boolean,
    aspectRatio: number,
    maxSize: Dimensions
  ): void {
    this.handleResizeByPosition(cropper, moveStart, diffX, diffY, cropperScaledMinWidth, cropperScaledMinHeight);

    if (maintainAspectRatio) {
      this.checkAspectRatio(cropper, moveStart, aspectRatio, maxSize);
    }
  }

  /**
   * Handles resize based on position
   */
  private handleResizeByPosition(
    cropper: CropperPosition,
    moveStart: MoveStart,
    diffX: number,
    diffY: number,
    cropperScaledMinWidth: number,
    cropperScaledMinHeight: number
  ): void {
    switch (moveStart.position) {
      case 'left':
        cropper.x1 = Math.min(moveStart.x1 + diffX, cropper.x2 - cropperScaledMinWidth);
        break;
      case 'topleft':
        cropper.x1 = Math.min(moveStart.x1 + diffX, cropper.x2 - cropperScaledMinWidth);
        cropper.y1 = Math.min(moveStart.y1 + diffY, cropper.y2 - cropperScaledMinHeight);
        break;
      case 'top':
        cropper.y1 = Math.min(moveStart.y1 + diffY, cropper.y2 - cropperScaledMinHeight);
        break;
      case 'topright':
        cropper.x2 = Math.max(moveStart.x2 + diffX, cropper.x1 + cropperScaledMinWidth);
        cropper.y1 = Math.min(moveStart.y1 + diffY, cropper.y2 - cropperScaledMinHeight);
        break;
      case 'right':
        cropper.x2 = Math.max(moveStart.x2 + diffX, cropper.x1 + cropperScaledMinWidth);
        break;
      case 'bottomright':
        cropper.x2 = Math.max(moveStart.x2 + diffX, cropper.x1 + cropperScaledMinWidth);
        cropper.y2 = Math.max(moveStart.y2 + diffY, cropper.y1 + cropperScaledMinHeight);
        break;
      case 'bottom':
        cropper.y2 = Math.max(moveStart.y2 + diffY, cropper.y1 + cropperScaledMinHeight);
        break;
      case 'bottomleft':
        cropper.x1 = Math.min(moveStart.x1 + diffX, cropper.x2 - cropperScaledMinWidth);
        cropper.y2 = Math.max(moveStart.y2 + diffY, cropper.y1 + cropperScaledMinHeight);
        break;
      case 'center':
        this.handleCenterResize(cropper, moveStart, cropperScaledMinWidth, cropperScaledMinHeight);
        break;
    }
  }

  /**
   * Handles center resize (pinch gesture)
   */
  private handleCenterResize(
    cropper: CropperPosition,
    moveStart: MoveStart,
    cropperScaledMinWidth: number,
    cropperScaledMinHeight: number
  ): void {
    const { scale } = moveStart as unknown as { scale: number };
    const newWidth: number = Math.abs(moveStart.x2 - moveStart.x1) * scale;
    const newHeight: number = Math.abs(moveStart.y2 - moveStart.y1) * scale;
    const { x1, y1 } = cropper;

    cropper.x1 = Math.min(moveStart.clientX - newWidth / 2, cropper.x2 - cropperScaledMinWidth);
    cropper.y1 = Math.min(moveStart.clientY - newHeight / 2, cropper.y2 - cropperScaledMinHeight);
    cropper.x2 = Math.max(moveStart.clientX + newWidth / 2, x1 + cropperScaledMinWidth);
    cropper.y2 = Math.max(moveStart.clientY + newHeight / 2, y1 + cropperScaledMinHeight);
  }

  /**
   * Checks and maintains aspect ratio during resize
   */
  private checkAspectRatio(
    cropper: CropperPosition,
    moveStart: MoveStart,
    aspectRatio: number,
    maxSize: Dimensions
  ): void {
    switch (moveStart.position) {
      case 'top':
        this.handleTopAspectRatio(cropper, aspectRatio, maxSize);
        break;
      case 'bottom':
        this.handleBottomAspectRatio(cropper, aspectRatio, maxSize);
        break;
      case 'topleft':
        this.handleTopLeftAspectRatio(cropper, aspectRatio, maxSize);
        break;
      case 'topright':
        this.handleTopRightAspectRatio(cropper, aspectRatio, maxSize);
        break;
      case 'right':
      case 'bottomright':
        this.handleRightAspectRatio(cropper, aspectRatio, maxSize);
        break;
      case 'left':
      case 'bottomleft':
        this.handleLeftAspectRatio(cropper, aspectRatio, maxSize);
        break;
      case 'center':
        this.handleCenterAspectRatio(cropper, aspectRatio, maxSize);
        break;
    }
  }

  private handleTopAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.x2 = cropper.x1 + (cropper.y2 - cropper.y1) * aspectRatio;
    const overflowX: number = Math.max(cropper.x2 - maxSize.width, 0);
    const overflowY: number = Math.max(0 - cropper.y1, 0);
    if (overflowX > 0 || overflowY > 0) {
      const adjustment: number = overflowY * aspectRatio > overflowX ? overflowY * aspectRatio : overflowX;
      cropper.x2 -= adjustment;
      cropper.y1 += overflowY * aspectRatio > overflowX ? overflowY : overflowX / aspectRatio;
    }
  }

  private handleBottomAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.x2 = cropper.x1 + (cropper.y2 - cropper.y1) * aspectRatio;
    const overflowX: number = Math.max(cropper.x2 - maxSize.width, 0);
    const overflowY: number = Math.max(cropper.y2 - maxSize.height, 0);
    if (overflowX > 0 || overflowY > 0) {
      const adjustment: number = overflowY * aspectRatio > overflowX ? overflowY * aspectRatio : overflowX;
      cropper.x2 -= adjustment;
      cropper.y2 -= overflowY * aspectRatio > overflowX ? overflowY : overflowX / aspectRatio;
    }
  }

  private handleTopLeftAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.y1 = cropper.y2 - (cropper.x2 - cropper.x1) / aspectRatio;
    const overflowX: number = Math.max(0 - cropper.x1, 0);
    const overflowY: number = Math.max(0 - cropper.y1, 0);
    if (overflowX > 0 || overflowY > 0) {
      const adjustment: number = overflowY * aspectRatio > overflowX ? overflowY * aspectRatio : overflowX;
      cropper.x1 += adjustment;
      cropper.y1 += overflowY * aspectRatio > overflowX ? overflowY : overflowX / aspectRatio;
    }
  }

  private handleTopRightAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.y1 = cropper.y2 - (cropper.x2 - cropper.x1) / aspectRatio;
    const overflowX: number = Math.max(cropper.x2 - maxSize.width, 0);
    const overflowY: number = Math.max(0 - cropper.y1, 0);
    if (overflowX > 0 || overflowY > 0) {
      const adjustment: number = overflowY * aspectRatio > overflowX ? overflowY * aspectRatio : overflowX;
      cropper.x2 -= adjustment;
      cropper.y1 += overflowY * aspectRatio > overflowX ? overflowY : overflowX / aspectRatio;
    }
  }

  private handleRightAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.y2 = cropper.y1 + (cropper.x2 - cropper.x1) / aspectRatio;
    const overflowX: number = Math.max(cropper.x2 - maxSize.width, 0);
    const overflowY: number = Math.max(cropper.y2 - maxSize.height, 0);
    if (overflowX > 0 || overflowY > 0) {
      const adjustment: number = overflowY * aspectRatio > overflowX ? overflowY * aspectRatio : overflowX;
      cropper.x2 -= adjustment;
      cropper.y2 -= overflowY * aspectRatio > overflowX ? overflowY : overflowX / aspectRatio;
    }
  }

  private handleLeftAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.y2 = cropper.y1 + (cropper.x2 - cropper.x1) / aspectRatio;
    const overflowX: number = Math.max(0 - cropper.x1, 0);
    const overflowY: number = Math.max(cropper.y2 - maxSize.height, 0);
    if (overflowX > 0 || overflowY > 0) {
      const adjustment: number = overflowY * aspectRatio > overflowX ? overflowY * aspectRatio : overflowX;
      cropper.x1 += adjustment;
      cropper.y2 -= overflowY * aspectRatio > overflowX ? overflowY : overflowX / aspectRatio;
    }
  }

  private handleCenterAspectRatio(cropper: CropperPosition, aspectRatio: number, maxSize: Dimensions): void {
    cropper.x2 = cropper.x1 + (cropper.y2 - cropper.y1) * aspectRatio;
    cropper.y2 = cropper.y1 + (cropper.x2 - cropper.x1) / aspectRatio;
    const overflowX1: number = Math.max(0 - cropper.x1, 0);
    const overflowX2: number = Math.max(cropper.x2 - maxSize.width, 0);
    const overflowY1: number = Math.max(cropper.y2 - maxSize.height, 0);
    const overflowY2: number = Math.max(0 - cropper.y1, 0);
    if (overflowX1 > 0 || overflowX2 > 0 || overflowY1 > 0 || overflowY2 > 0) {
      cropper.x1 += overflowY1 * aspectRatio > overflowX1 ? overflowY1 * aspectRatio : overflowX1;
      cropper.x2 -= overflowY2 * aspectRatio > overflowX2 ? overflowY2 * aspectRatio : overflowX2;
      cropper.y1 += overflowY2 * aspectRatio > overflowX2 ? overflowY2 : overflowX2 / aspectRatio;
      cropper.y2 -= overflowY1 * aspectRatio > overflowX1 ? overflowY1 : overflowX1 / aspectRatio;
    }
  }
}
